import { SpeciesSelectInput } from "@/components/collection/SpeciesSelectInput"
import { PhotoChanges, PhotosForm } from "@/components/common/PhotosForm"
import { AudioChanges, AudiosForm } from "@/components/common/AudiosForm"
import { useAuth } from "@/hooks/useAuth"
import { useNetwork } from "@/hooks/useNetwork"
import { usePhotosMutate } from "@/hooks/usePhotosMutate"
import { useAudiosMutate } from "@/hooks/useAudiosMutate"
import { FullScoutingNote, useScoutingNote } from "@/hooks/useScoutingNote"
import { useScoutingNoteUpdate } from "@/hooks/useScoutingNoteUpdate"
import { zodResolver } from "@hookform/resolvers/zod"
import { ROLE, UpdateScoutingNote } from "@nasti/common/types"
import { Button } from "@nasti/ui/button"
import { Input } from "@nasti/ui/input"
import { Label } from "@nasti/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@nasti/ui/popover"
import { PhenologyRangeInput } from "@nasti/ui/phenologyRangeInput"
import { Switch } from "@nasti/ui/switch"
import { Textarea } from "@nasti/ui/textarea"
import { cn } from "@nasti/ui/utils"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { ChevronLeft, InfoIcon, X } from "lucide-react"
import { useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import * as z from "zod"
import { PersonMultiSelectField } from "@/components/common/PersonMultiSelectField"
import { ExtraFieldsAccordion } from "@/components/common/ExtraFieldsAccordion"
import { useUnsavedChangesPrompt } from "@/hooks/useUnsavedChangesPrompt"
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog"
import { hasMediaChanges } from "@/lib/mediaChanges"

// --- Schema & Types ---
const stringToNumber = z.preprocess(
  (val) => {
    if (typeof val === "string" && val.trim() === "") return null
    const num = Number(val)
    return isNaN(num) ? undefined : num
  },
  z.number({ message: "Please enter a valid number" }).nullable(),
)

const schema = z
  .object({
    species_id: z.preprocess(
      (val) => (val === "" ? null : val),
      z.string().nullable(),
    ),
    species_uncertain: z.boolean(),
    field_name: z
      .string()
      .nullable()
      .transform((val) => val || ""),
    specimen_collected: z.boolean(),
    description: z
      .string()
      .optional()
      .transform((val) => val || ""),
    latitude: stringToNumber,
    longitude: stringToNumber,
    person_ids: z.array(z.string().uuid()).default([]),
    phenology_start: z.number().min(-100).max(100).nullable(),
    phenology_peak: z.number().min(-100).max(100).nullable(),
    phenology_end: z.number().min(-100).max(100).nullable(),
  })
  .refine(
    (data) => Boolean(data.species_id) || data.field_name.trim().length > 0,
    {
      message: "Field name is required when no species is selected",
      path: ["field_name"],
    },
  )

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  species_id: null,
  species_uncertain: false,
  field_name: "",
  latitude: null,
  longitude: null,
  specimen_collected: false,
  description: "",
  person_ids: [],
  phenology_start: null,
  phenology_peak: null,
  phenology_end: null,
}

export const Route = createFileRoute(
  "/_private/trips/$id/scouting-notes/$scoutingNoteId/edit",
)({
  component: ScoutingNoteForm,
})

function ScoutingNoteForm() {
  const { scoutingNoteId, id: tripId } = useParams({
    from: "/_private/trips/$id/scouting-notes/$scoutingNoteId/edit",
  })
  const scoutingNote = useScoutingNote({ scoutingNoteId })

  const navigate = useNavigate({
    from: "/trips/$id/scouting-notes/$scoutingNoteId/edit",
  })

  const handleBackClick = () => {
    navigate({
      to: "/trips/$id/scouting-notes/$scoutingNoteId",
      params: { id: tripId, scoutingNoteId },
    })
  }

  if (!scoutingNote)
    return (
      <div className="flex h-full w-full flex-col items-start">
        <h2 className="p-2 text-2xl">No scouting note available</h2>
        <Button
          className="flex w-full justify-start text-lg"
          variant="ghost"
          onClick={handleBackClick}
        >
          <ChevronLeft className="h-5 w-5" /> Back
        </Button>
      </div>
    )

  return (
    <ScoutingNoteFormReady
      scoutingNote={scoutingNote}
      scoutingNoteId={scoutingNoteId}
      tripId={tripId}
    />
  )
}

function ScoutingNoteFormReady({
  scoutingNote,
  scoutingNoteId,
  tripId,
}: {
  scoutingNote: FullScoutingNote
  scoutingNoteId: string
  tripId: string
}) {
  const { user, organisation, role } = useAuth()

  const {
    photos: initialPhotos,
    audios: initialAudios,
    species,
    ...initialValues
  } = scoutingNote

  const { mutateAsync: updateScoutingNote } = useScoutingNoteUpdate({ tripId })
  const { createPhotoMutation, updateCaptionMutation, deletePhotoMutation } =
    usePhotosMutate({
      entityId: scoutingNoteId,
      entityType: "scoutingNote",
      tripId,
    })
  const {
    createAudioMutation,
    deleteAudioMutation,
    updateCaptionMutation: updateAudioCaptionMutation,
  } = useAudiosMutate({
    entityId: scoutingNoteId,
    entityType: "scoutingNote",
    tripId,
  })

  const { isOnline } = useNetwork()

  const navigate = useNavigate({
    from: "/trips/$id/scouting-notes/$scoutingNoteId/edit",
  })

  const handleBackClick = () => {
    navigate({
      to: "/trips/$id/scouting-notes/$scoutingNoteId",
      params: { id: tripId, scoutingNoteId },
    })
  }

  // Unique ID: use provided or generate new
  const scoutingNoteIdRef = useRef<string>(initialValues.id)

  // Photo state
  const [photoChanges, setPhotoChanges] = useState<PhotoChanges>({
    add: [],
    keep: initialPhotos,
  })

  // Audio state
  const [audioChanges, setAudioChanges] = useState<AudioChanges>({
    add: [],
    keep: initialAudios,
  })

  const defaultValues = schema.parse({
    ...DEFAULT_VALUES,
    ...initialValues,
    latitude: initialValues.locationCoord?.latitude ?? null,
    longitude: initialValues.locationCoord?.longitude ?? null,
  })

  // Form setup
  const {
    watch,
    setValue,
    register,
    handleSubmit: hookSubmit,
    formState: { isValid, isSubmitting, errors, isDirty },
    control,
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
  })

  const hasUnsavedChanges =
    isDirty ||
    hasMediaChanges(initialPhotos, photoChanges) ||
    hasMediaChanges(initialAudios, audioChanges)

  const { isPromptOpen, discardChanges, keepEditing, allowNavigation } =
    useUnsavedChangesPrompt({ hasUnsavedChanges })

  // Field name entry toggle
  const isFieldName =
    watch("specimen_collected") || Boolean(initialValues.field_name)
  const [enterFieldName, setEnterFieldName] = useState(isFieldName)

  const handleSetIsSpecimenCollected = (val: boolean) => {
    setValue("specimen_collected", val)
    setEnterFieldName(val)
  }

  // Handlers
  const onFormSubmit = async (data: FormValues) => {
    if (!user || !organisation) throw new Error("Not logged in")
    if (!tripId) throw new Error("tripId must be supplied")

    const { latitude, longitude, ...rest } = data
    const locationPoint = `POINT(${longitude} ${latitude})`

    const payload: UpdateScoutingNote = {
      id: scoutingNoteIdRef.current,
      trip_id: tripId,
      organisation_id: organisation.id,
      created_by: user.id,
      created_at: new Date().toISOString(),
      location: locationPoint,
      ...rest,
    }
    console.log({ payload })
    if (!hasUnsavedChanges) {
      allowNavigation()
      return navigate({
        to: "/trips/$id/scouting-notes/$scoutingNoteId",
        params: { id: tripId, scoutingNoteId },
      })
    }

    const updatePromise = updateScoutingNote(payload)
    if (isOnline) await updatePromise
    await Promise.all(
      photoChanges.add.map((photo) =>
        createPhotoMutation.mutateAsync(photo, { onError: console.error }),
      ),
    )
    await Promise.all(
      audioChanges.add.map((audio) =>
        createAudioMutation.mutateAsync(audio, { onError: console.error }),
      ),
    )
    // find which photos have been removed from the initial list
    const removedPhotos = initialPhotos.filter(
      (photo) => !photoChanges.keep.find((p) => p.id === photo.id),
    )
    const deletePhotoPromises = removedPhotos.map((photo) =>
      deletePhotoMutation.mutateAsync(photo.id, { onError: console.error }),
    )

    // for the remaining photos, update captions if they have changed
    const changedPhotos = photoChanges.keep.filter((kept) => {
      const existing = initialPhotos.find((p) => p.id === kept.id)
      return existing?.caption !== kept.caption
    })

    const changePhotoPromises = changedPhotos.map((photo) =>
      updateCaptionMutation.mutateAsync({
        photoId: photo.id,
        caption: photo.caption,
      }),
    )

    // removed + caption-changed audio
    const removedAudios = initialAudios.filter(
      (audio) => !audioChanges.keep.find((a) => a.id === audio.id),
    )
    const deleteAudioPromises = removedAudios.map((audio) =>
      deleteAudioMutation.mutateAsync(audio.id, { onError: console.error }),
    )
    const changedAudios = audioChanges.keep.filter((kept) => {
      const existing = initialAudios.find((a) => a.id === kept.id)
      return existing?.caption !== kept.caption
    })
    const changeAudioPromises = changedAudios.map((audio) =>
      updateAudioCaptionMutation.mutateAsync({
        audioId: audio.id,
        caption: audio.caption,
      }),
    )

    if (isOnline) {
      // do not await the add photo Promises - they're slower and can happen in parallel
      // await Promise.all(addPhotoPromises)
      await Promise.all(deletePhotoPromises)
      await Promise.all(changePhotoPromises)
      await Promise.all(deleteAudioPromises)
      await Promise.all(changeAudioPromises)
    }

    allowNavigation()
    return navigate({
      to: "/trips/$id/scouting-notes/$scoutingNoteId",
      params: { id: tripId, scoutingNoteId },
    })
  }

  const speciesId = watch("species_id")
  const [descriptionFocus, setDescriptionFocus] = useState(false)

  // You shouldn't be here
  if (initialValues.created_by !== user?.id && role !== ROLE.ADMIN) {
    navigate({
      to: "/trips/$id/scouting-notes/$scoutingNoteId",
      params: { id: tripId, scoutingNoteId },
    })
    return null
  }

  return (
    <div className="flex h-full flex-col">
      <h2 className="p-2 text-2xl">{"Edit Scouting Note"}</h2>
      <form
        className="flex flex-1 flex-col justify-between"
        onSubmit={hookSubmit(onFormSubmit)}
      >
        <div className="space-y-4 overflow-y-auto px-2">
          <SpeciesSelectInput
            onSelectSpecies={(speciesId) =>
              setValue("species_id", speciesId, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            tripId={tripId}
            selectedSpeciesId={speciesId ?? undefined}
          />
          {enterFieldName && (
            <div>
              <Label>Specimen Name</Label>
              <div className="flex w-full items-center space-x-2">
                <Input
                  {...register("field_name")}
                  className="h-12 text-lg"
                  autoComplete="off"
                  tabIndex={1}
                  autoFocus
                />
                <Button
                  onClick={(e) => {
                    e.preventDefault()
                    setValue("field_name", "", { shouldValidate: true })
                  }}
                  className="h-12"
                  variant={"outline"}
                >
                  <X />
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Switch id="species_uncertain" {...register("species_uncertain")} />

            <Label>Species Uncertain?</Label>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="h-5 w-5" />
              </PopoverTrigger>
              <PopoverContent>
                Toggle if not 100% certain or after entering field name
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="specimen_collected"
              render={({ field: { value } }) => (
                <Switch
                  id="specimen_collected"
                  checked={value}
                  onCheckedChange={handleSetIsSpecimenCollected}
                />
              )}
            />
            <Label>Specimen Collected?</Label>
            <Popover>
              <PopoverTrigger asChild>
                <InfoIcon className="h-5 w-5" />
              </PopoverTrigger>
              <PopoverContent>
                Select if you physically collected the specimen
              </PopoverContent>
            </Popover>
          </div>

          <Controller
            control={control}
            name="person_ids"
            render={({ field }) => (
              <PersonMultiSelectField
                organisationId={initialValues.organisation_id}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Latitude</Label>
              <Input
                {...register("latitude")}
                className={errors.latitude ? "border-amber-600" : ""}
              />
              {errors.latitude && (
                <p className="text-amber-600">{errors.latitude.message}</p>
              )}
            </div>
            <div>
              <Label>Longitude</Label>
              <Input
                {...register("longitude")}
                className={errors.longitude ? "border-amber-600" : ""}
              />
              {errors.longitude && (
                <p className="text-amber-600">{errors.longitude.message}</p>
              )}
            </div>
          </div>

          <ExtraFieldsAccordion>
            <Controller
              control={control}
              name="phenology_start"
              render={({ field: startField }) => (
                <Controller
                  control={control}
                  name="phenology_peak"
                  render={({ field: peakField }) => (
                    <Controller
                      control={control}
                      name="phenology_end"
                      render={({ field: endField }) => (
                        <PhenologyRangeInput
                          value={[
                            startField.value,
                            peakField.value,
                            endField.value,
                          ]}
                          onValueChange={([start, peak, end]) => {
                            startField.onChange(start)
                            peakField.onChange(peak)
                            endField.onChange(end)
                          }}
                        />
                      )}
                    />
                  )}
                />
              )}
            />

            <PhotosForm
              initialPhotos={initialPhotos}
              onPhotosChange={setPhotoChanges}
            />
            <div>
              <Label>Description</Label>
              <Textarea
                {...register("description")}
                className={cn(
                  "transition-all",
                  descriptionFocus ? "h-40" : "h-20",
                )}
                onFocus={() => setDescriptionFocus(true)}
                onBlur={() => setDescriptionFocus(false)}
              />
            </div>

            <AudiosForm
              initialAudios={initialAudios}
              onAudiosChange={setAudioChanges}
            />
          </ExtraFieldsAccordion>
        </div>

        <div className="flex space-x-2 border-t p-2">
          <Button
            className="w-full"
            variant="secondary"
            onClick={handleBackClick}
          >
            Cancel
          </Button>
          <Button
            className="w-full"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
      <UnsavedChangesDialog
        open={isPromptOpen}
        onDiscard={discardChanges}
        onKeepEditing={keepEditing}
      />
    </div>
  )
}
